import { Fragment, useId, useState } from 'react'
import { Star } from 'lucide-react'
import type { SurveyAnswer, SurveyFormArea, SurveyQuestion } from '../../types'

/** A partner team's name as a small pill, where a question asks about it. */
export function TeamTag({ name }: { name: string }) {
  return <span className="inline-flex items-center rounded-md bg-[#005EB8]/10 text-[#005EB8] px-1.5 text-[12.5px] leading-5 font-semibold whitespace-nowrap">{name}</span>
}

/** The team the questions are about, shown once: its name, then the
 *  departments it supports, so it's clear who it is, with the person's own
 *  (`mine`) marked "you". In the survey's blue header, or `light` on white
 *  under the area question of a split department. */
export function TeamBanner({ team, supports, mine, light }: { team: string; supports: string[]; mine?: string; light?: boolean }) {
  return (
    <div className="mt-4">
      <div className={`text-[11px] uppercase tracking-wide font-semibold mb-1.5 ${light ? 'text-slate-400' : 'text-white/70'}`}>Your IT partner team</div>
      <div className={`inline-flex max-w-full rounded-lg overflow-hidden ring-1 text-[13px] leading-7 ${light ? 'ring-[#005EB8]/20' : 'ring-white/25'}`}>
        <span className={`flex-none font-bold px-3 ${light ? 'bg-[#005EB8] text-white' : 'bg-white text-[#005EB8]'}`}>{team}</span>
        {supports.length > 0 && (
          <span className={`px-3 ${light ? 'bg-[#005EB8]/[0.06] text-[#005EB8]' : 'bg-white/10 text-white'}`}>
            {supports.map((name, i) => (
              <Fragment key={name}>
                {i > 0 && ' · '}
                {name === mine ? (
                  <span className="font-bold">
                    {name}
                    <span className={`ml-1 relative -top-px rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide ${light ? 'bg-[#005EB8]/15' : 'bg-white/25'}`}>you</span>
                  </span>
                ) : name}
              </Fragment>
            ))}
          </span>
        )}
      </div>
    </div>
  )
}

type Partner = { team?: string; supports?: string[]; partner: string }

/** A question's text with {partner} as the team's tag, or as plain words
 *  when there is no team (the fallback, "IT"). */
function withPartner(text: string, partner?: Partner) {
  if (!partner || !text.includes('{partner}')) return text
  return text.split('{partner}').flatMap((part, i) => (i === 0 ? [part] : [
    partner.team ? <TeamTag key={i} name={partner.team} /> : partner.partner,
    part,
  ]))
}

/**
 * One question as the person answering sees it: its number, title, text and
 * the right input for its kind. Shared by the public survey page and the
 * preview in Settings, so the preview is the real thing.
 */
export function QuestionBlock({ question, index, value, onChange, partner }: {
  question: SurveyQuestion
  index: number
  value: SurveyAnswer | undefined
  onChange: (v: SurveyAnswer) => void
  /** Who {partner} is: the team for its tag, and the words otherwise. */
  partner?: Partner
}) {
  // The question names its group of answers, and the written-answer box.
  const id = useId()
  return (
    <div className="mb-7" role="group" aria-labelledby={id}>
      <div id={id} className="block text-sm font-semibold text-slate-800 mb-3">
        {question.title && <span className="block text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1">{index + 1}. {question.title}</span>}
        {!question.title && <>{index + 1}. </>}{withPartner(question.text, partner)}
        {question.required && <span className="text-red-500"> *</span>}
      </div>
      {question.kind === 'rating' && <Stars question={question} value={value} onChange={onChange} />}
      {question.kind === 'scale' && <Scale question={question} value={value} onChange={onChange} />}
      {question.kind === 'choice' && <Choice question={question} value={value} onChange={onChange} />}
      {question.kind === 'text' && (
        <textarea aria-labelledby={id} value={(value as string) ?? ''} onChange={e => onChange(e.target.value)} rows={3} maxLength={4000}
          className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50/50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#005EB8]/20 focus:border-[#005EB8]/40 resize-none" />
      )}
    </div>
  )
}

/** For someone in a split department: the one area they give feedback on,
 *  each with what it covers and the IT team behind it. */
export function AreaChoice({ areas, value, onChange }: {
  areas: Pick<SurveyFormArea, 'id' | 'name' | 'definition' | 'team' | 'supports'>[]
  value: string
  onChange: (id: string) => void
}) {
  const chosen = areas.find(a => a.id === value)
  return (
    <div className="mb-7">
      <div className="block text-sm font-semibold text-slate-800 mb-3">
        <span className="block text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Your area</span>
        Choose one area to leave feedback for<span className="text-red-500"> *</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Your area">
        {areas.map(area => {
          const on = area.id === value
          return (
            <button key={area.id} type="button" role="radio" aria-checked={on} onClick={() => onChange(area.id)}
              className={`text-left flex gap-3 rounded-xl border-2 px-4 py-3.5 transition-all ${on ? 'border-[#00AEEF] bg-[#00AEEF]/10' : 'border-slate-200 hover:border-[#00AEEF]'}`}>
              <span className={`mt-0.5 w-[18px] h-[18px] flex-none rounded-full border-2 flex items-center justify-center ${on ? 'border-[#00AEEF]' : 'border-slate-300'}`}>
                {on && <span className="w-2 h-2 rounded-full bg-[#00AEEF]" />}
              </span>
              <span className="block">
                <span className={`block text-sm font-bold ${on ? 'text-[#0077A8]' : 'text-slate-700'}`}>{area.name}</span>
                {area.definition && <span className="block text-[13px] leading-snug text-slate-600 mt-1">{area.definition}</span>}
                {area.team && <span className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-2">IT team <TeamTag name={area.team} /></span>}
              </span>
            </button>
          )
        })}
      </div>
      {chosen?.team && <TeamBanner light team={chosen.team} supports={chosen.supports ?? []} mine={chosen.name} />}
    </div>
  )
}

type Input = { question: SurveyQuestion; value: SurveyAnswer | undefined; onChange: (v: SurveyAnswer) => void }

const chip = (on: boolean) =>
  `py-2.5 border-2 rounded-xl text-sm font-bold transition-all ${on
    ? 'border-[#00AEEF] bg-[#00AEEF]/10 text-[#00AEEF]'
    : 'border-slate-200 text-slate-600 hover:border-[#00AEEF] hover:text-[#00AEEF]'}`

function NotApplicable({ question, value, onChange }: Input) {
  if (!question.naLabel) return null
  return (
    <button type="button" aria-pressed={value === 'na'} onClick={() => onChange('na')} className={`${chip(value === 'na')} px-4`}>
      {question.naLabel}
    </button>
  )
}

function Stars({ question, value, onChange }: Input) {
  const [hover, setHover] = useState(0)
  const chosen = typeof value === 'number' ? value : 0
  const shown = hover || chosen
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {question.labels.map((label, i) => (
          <button key={`${label}-${i}`} type="button" aria-label={label} title={label} aria-pressed={chosen === i + 1}
            onMouseEnter={() => setHover(i + 1)} onClick={() => onChange(i + 1)} className="p-1">
            <Star className={`w-8 h-8 transition-colors ${i < shown ? 'fill-[#F88806] text-[#F88806]' : 'text-slate-300'}`} />
          </button>
        ))}
      </div>
      <span className="text-sm text-slate-600 min-w-[12rem]">{shown ? question.labels[shown - 1] : ''}</span>
      <NotApplicable question={question} value={value} onChange={onChange} />
    </div>
  )
}

function Scale({ question, value, onChange }: Input) {
  return (
    <div>
      <div className="grid grid-cols-11 gap-1.5">
        {Array.from({ length: 11 }, (_, n) => (
          <button key={n} type="button" aria-pressed={value === n} onClick={() => onChange(n)} className={chip(value === n)}>{n}</button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-400 mt-2 px-1">
        <span>{question.lowLabel && `0 - ${question.lowLabel}`}</span>
        <span>{question.highLabel && `10 - ${question.highLabel}`}</span>
      </div>
      {question.naLabel && <div className="mt-2"><NotApplicable question={question} value={value} onChange={onChange} /></div>}
    </div>
  )
}

function Choice({ question, value, onChange }: Input) {
  return (
    <div className="flex flex-wrap gap-2">
      {question.options.map(option => (
        <button key={option} type="button" aria-pressed={value === option} onClick={() => onChange(option)} className={`${chip(value === option)} px-4`}>{option}</button>
      ))}
      <NotApplicable question={question} value={value} onChange={onChange} />
    </div>
  )
}
